import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface A3Data {
  a3Id: string;
  title: string;
  industrialEngineer: string;
  siteDc: string;
  region: string;
  status: string;
  date: string;
  validatedSavings?: number;
  tags?: string;

  defineProblem?: {
    background: string;
    problemStatement: string;
  };

  currentState?: {
    description: string;
    metric1: string;
    metric2: string;
    metric1Unfavorable?: boolean;
    metric2Unfavorable?: boolean;
  };

  goalTargetState?: {
    goalStatement: string;
    targetMetric: string;
    targetDate: string;
  };

  causeAnalysis?: {
    cause: string;
    category: string;
    evidence: string;
  }[];

  countermeasures?: {
    countermeasure: string;
    linkedCause: string;
    owner: string;
  }[];

  actionPlan?: {
    action: string;
    owner: string;
    dueDate: string;
    status: string;
  }[];

  resultsAndLessonsLearned?: {
    resultsSummary: string;
    lessonsLearned: {
      lesson: string;
      category: string;
    }[];
  };
}

// Helper: Draw section header with number
const drawSectionHeader = (
  doc: jsPDF,
  options: {
    x: number;
    y: number;
    width: number;
    num: string;
    title: string;
    headerBg: [number, number, number];
    accentColor: [number, number, number];
    fontSize: { section: number; small: number };
  }
) => {
  const { x, y, width, num, title, headerBg, accentColor, fontSize } = options;
  
  doc.setFillColor(...headerBg);
  doc.rect(x, y, width, 8, 'F');
  
  // Number circle
  doc.setFillColor(...accentColor);
  doc.circle(x + 4, y + 4, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(fontSize.small);
  doc.setFont('helvetica', 'bold');
  doc.text(num, x + 4, y + 5.2, { align: 'center' });
  
  // Title
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(fontSize.section);
  doc.text(title, x + 10, y + 5.5);
  doc.setFont('helvetica', 'normal');
  
  return y + 8;
};

// Helper: Wrap text and return lines
const wrapText = (doc: jsPDF, text: string, maxWidth: number): string[] => {
  if (!text) return [''];
  return doc.splitTextToSize(text, maxWidth);
};

// Helper: Draw header section
const drawHeaderSection = (
  doc: jsPDF,
  a3Data: A3Data,
  margin: number,
  contentWidth: number,
  accentColor: [number, number, number],
  greenColor: [number, number, number],
  fontSizes: { title: number; small: number; content: number }
): number => {
  let yPos = margin;
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Title
  doc.setFontSize(fontSizes.title);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentColor);
  const titleLines = wrapText(doc, a3Data.title || 'Untitled A3', contentWidth);
  titleLines.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 7;
  });
  
  // Subtitle
  doc.setFontSize(fontSizes.small);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'italic');
  doc.text('Continuous Improvement Project', margin, yPos);
  yPos += 8;

  // Project Info Row
  doc.setFontSize(fontSizes.content);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  
  const infoColWidth = contentWidth / 4;
  let xOffset = margin;
  
  // Industrial Engineer
  doc.setFont('helvetica', 'bold');
  doc.text('Industrial Engineer:', xOffset, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(a3Data.industrialEngineer || 'N/A', xOffset, yPos + 4);
  
  // Site/DC
  xOffset += infoColWidth;
  doc.setFont('helvetica', 'bold');
  doc.text('Site/DC:', xOffset, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(a3Data.siteDc || 'N/A', xOffset, yPos + 4);
  
  // Date
  xOffset += infoColWidth;
  doc.setFont('helvetica', 'bold');
  doc.text('Date:', xOffset, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(a3Data.date || 'N/A', xOffset, yPos + 4);
  
  // Status
  xOffset += infoColWidth;
  doc.setFont('helvetica', 'bold');
  doc.text('Status:', xOffset, yPos);
  doc.setFont('helvetica', 'normal');
  
  const statusText = a3Data.status || 'N/A';
  if (statusText.toLowerCase() === 'completed') {
    doc.setTextColor(...greenColor);
  } else if (statusText.toLowerCase() === 'in progress') {
    doc.setTextColor(234, 179, 8);
  }
  doc.text(statusText, xOffset, yPos + 4);
  doc.setTextColor(0, 0, 0);
  
  yPos += 10;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;
  
  return yPos;
};

// Helper: Render metric badge
const renderMetricBadge = (
  doc: jsPDF,
  x: number,
  y: number,
  text: string,
  isUnfavorable: boolean | undefined,
  redColor: [number, number, number],
  fontSize: number
) => {
  if (isUnfavorable) {
    doc.setFillColor(...redColor);
    doc.setTextColor(255, 255, 255);
  } else {
    doc.setFillColor(226, 232, 240);
    doc.setTextColor(0, 0, 0);
  }
  doc.roundedRect(x, y, 60, 6, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(fontSize);
  doc.text(text, x + 2, y + 4);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
};

// Helper: Render section 1 - Define the Problem
const renderDefineProblemSection = (
  doc: jsPDF,
  a3Data: A3Data,
  leftColX: number,
  leftY: number,
  colWidth: number,
  lineHeight: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  leftY = drawSectionHeader(doc, {
    x: leftColX,
    y: leftY,
    width: colWidth,
    num: '1',
    title: 'Define the Problem',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  doc.setFontSize(fontSizes.content);
  
  if (a3Data.defineProblem?.problemStatement) {
    const problemLines = wrapText(doc, a3Data.defineProblem.problemStatement, colWidth - 4);
    problemLines.forEach(line => {
      doc.text(line, leftColX + 2, leftY + 4);
      leftY += lineHeight;
    });
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('Not provided', leftColX + 2, leftY + 4);
    doc.setTextColor(0, 0, 0);
    leftY += lineHeight;
  }
  
  if (a3Data.defineProblem?.background) {
    leftY += 2;
    doc.setFontSize(fontSizes.content);
    doc.setFont('helvetica', 'bold');
    doc.text('Background:', leftColX + 2, leftY + 4);
    doc.setFont('helvetica', 'normal');
    leftY += lineHeight;
    const bgLines = wrapText(doc, a3Data.defineProblem.background, colWidth - 4);
    bgLines.forEach(line => {
      doc.text(line, leftColX + 2, leftY + 2);
      leftY += lineHeight;
    });
  }
  
  return leftY + 6;
};

// Helper: Render section 2 - Current State
const renderCurrentStateSection = (
  doc: jsPDF,
  a3Data: A3Data,
  leftColX: number,
  leftY: number,
  colWidth: number,
  lineHeight: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  redColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  leftY = drawSectionHeader(doc, {
    x: leftColX,
    y: leftY,
    width: colWidth,
    num: '2',
    title: 'Current State',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  doc.setFontSize(fontSizes.content);
  
  if (a3Data.currentState?.description) {
    const descLines = wrapText(doc, a3Data.currentState.description, colWidth - 4);
    descLines.forEach(line => {
      doc.text(line, leftColX + 2, leftY + 4);
      leftY += lineHeight;
    });
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('Not provided', leftColX + 2, leftY + 4);
    doc.setTextColor(0, 0, 0);
    leftY += lineHeight;
  }
  
  // Metrics
  if (a3Data.currentState?.metric1 || a3Data.currentState?.metric2) {
    leftY += 3;
    let metricX = leftColX + 2;
    
    if (a3Data.currentState.metric1) {
      renderMetricBadge(doc, metricX, leftY, a3Data.currentState.metric1, a3Data.currentState.metric1Unfavorable, redColor, fontSizes.content);
      metricX += 65;
    }
    
    if (a3Data.currentState.metric2) {
      renderMetricBadge(doc, metricX, leftY, a3Data.currentState.metric2, a3Data.currentState.metric2Unfavorable, redColor, fontSizes.content);
    }
    
    leftY += 8;
  }
  
  doc.setFontSize(fontSizes.content);
  return leftY + 4;
};

// Helper: Render section 3 - Goal/Target State
const renderGoalTargetStateSection = (
  doc: jsPDF,
  a3Data: A3Data,
  leftColX: number,
  leftY: number,
  colWidth: number,
  lineHeight: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  greenColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  leftY = drawSectionHeader(doc, {
    x: leftColX,
    y: leftY,
    width: colWidth,
    num: '3',
    title: 'Goal / Target State',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  doc.setFontSize(fontSizes.content);
  
  if (a3Data.goalTargetState?.goalStatement) {
    const goalLines = wrapText(doc, a3Data.goalTargetState.goalStatement, colWidth - 4);
    goalLines.forEach(line => {
      doc.text(line, leftColX + 2, leftY + 4);
      leftY += lineHeight;
    });
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('Not provided', leftColX + 2, leftY + 4);
    doc.setTextColor(0, 0, 0);
    leftY += lineHeight;
  }
  
  // Target metrics
  if (a3Data.goalTargetState?.targetMetric || a3Data.goalTargetState?.targetDate) {
    leftY += 3;
    let targetX = leftColX + 2;
    
    if (a3Data.goalTargetState.targetMetric) {
      doc.setFillColor(...greenColor);
      doc.setTextColor(255, 255, 255);
      doc.roundedRect(targetX, leftY, 60, 6, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSizes.content);
      doc.text(a3Data.goalTargetState.targetMetric, targetX + 2, leftY + 4);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      targetX += 65;
    }
    
    if (a3Data.goalTargetState.targetDate) {
      doc.setFillColor(226, 232, 240);
      doc.roundedRect(targetX, leftY, 60, 6, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSizes.content);
      doc.text(a3Data.goalTargetState.targetDate, targetX + 2, leftY + 4);
      doc.setFont('helvetica', 'normal');
    }
    
    leftY += 8;
  }
  
  doc.setFontSize(fontSizes.content);
  return leftY + 4;
};

// Helper: Render section 4 - Cause Analysis
const renderCauseAnalysisSection = (
  doc: jsPDF,
  a3Data: A3Data,
  rightColX: number,
  rightY: number,
  colWidth: number,
  pageWidth: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  rightY = drawSectionHeader(doc, {
    x: rightColX,
    y: rightY,
    width: colWidth,
    num: '4',
    title: 'Cause Analysis',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  
  if (a3Data.causeAnalysis && a3Data.causeAnalysis.length > 0) {
    autoTable(doc, {
      startY: rightY,
      margin: { left: rightColX, right: pageWidth - rightColX - colWidth },
      head: [['Cause', 'Category', 'Evidence']],
      body: a3Data.causeAnalysis.map(c => [c.cause || '', c.category || '', c.evidence || '']),
      theme: 'grid',
      headStyles: { fillColor: accentColor, fontSize: fontSizes.content, fontStyle: 'bold' },
      bodyStyles: { fontSize: fontSizes.content },
      columnStyles: {
        0: { cellWidth: colWidth * 0.35 },
        1: { cellWidth: colWidth * 0.20 },
        2: { cellWidth: colWidth * 0.45 }
      }
    });
    rightY = (doc as any).lastAutoTable.finalY + 5;
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('No causes logged', rightColX + 2, rightY + 4);
    doc.setTextColor(0, 0, 0);
    rightY += 10;
  }
  
  return rightY;
};

// Helper: Render section 5 - Countermeasures
const renderCountermeasuresSection = (
  doc: jsPDF,
  a3Data: A3Data,
  rightColX: number,
  rightY: number,
  colWidth: number,
  pageWidth: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  rightY = drawSectionHeader(doc, {
    x: rightColX,
    y: rightY,
    width: colWidth,
    num: '5',
    title: 'Countermeasures',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  
  if (a3Data.countermeasures && a3Data.countermeasures.length > 0) {
    autoTable(doc, {
      startY: rightY,
      margin: { left: rightColX, right: pageWidth - rightColX - colWidth },
      head: [['Countermeasure', 'Linked Cause', 'Owner']],
      body: a3Data.countermeasures.map(c => [c.countermeasure || '', c.linkedCause || '', c.owner || '']),
      theme: 'grid',
      headStyles: { fillColor: accentColor, fontSize: fontSizes.content, fontStyle: 'bold' },
      bodyStyles: { fontSize: fontSizes.content },
      columnStyles: {
        0: { cellWidth: colWidth * 0.50 },
        1: { cellWidth: colWidth * 0.30 },
        2: { cellWidth: colWidth * 0.20 }
      }
    });
    rightY = (doc as any).lastAutoTable.finalY + 5;
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('No countermeasures logged', rightColX + 2, rightY + 4);
    doc.setTextColor(0, 0, 0);
    rightY += 10;
  }
  
  return rightY;
};

// Helper: Render section 6 - Action Plan
const renderActionPlanSection = (
  doc: jsPDF,
  a3Data: A3Data,
  margin: number,
  fullWidthY: number,
  contentWidth: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  fullWidthY = drawSectionHeader(doc, {
    x: margin,
    y: fullWidthY,
    width: contentWidth,
    num: '6',
    title: 'Action Plan',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  
  if (a3Data.actionPlan && a3Data.actionPlan.length > 0) {
    autoTable(doc, {
      startY: fullWidthY,
      head: [['Action', 'Owner', 'Due Date', 'Status']],
      body: a3Data.actionPlan.map(a => [a.action || '', a.owner || '', a.dueDate || '', a.status || '']),
      theme: 'grid',
      headStyles: { fillColor: accentColor, fontSize: fontSizes.content, fontStyle: 'bold' },
      bodyStyles: { fontSize: fontSizes.content },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.48 },
        1: { cellWidth: contentWidth * 0.18 },
        2: { cellWidth: contentWidth * 0.18 },
        3: { cellWidth: contentWidth * 0.16 }
      }
    });
    fullWidthY = (doc as any).lastAutoTable.finalY + 5;
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('No actions logged', margin + 2, fullWidthY + 4);
    doc.setTextColor(0, 0, 0);
    fullWidthY += 10;
  }
  
  return fullWidthY;
};

// Helper: Render section 7 - Results & Lessons Learned
const renderResultsAndLessonsSection = (
  doc: jsPDF,
  a3Data: A3Data,
  margin: number,
  fullWidthY: number,
  contentWidth: number,
  pageWidth: number,
  lineHeight: number,
  headerBg: [number, number, number],
  accentColor: [number, number, number],
  fontSizes: { section: number; small: number; content: number }
): number => {
  fullWidthY = drawSectionHeader(doc, {
    x: margin,
    y: fullWidthY,
    width: contentWidth,
    num: '7',
    title: 'Results & Lessons Learned',
    headerBg,
    accentColor,
    fontSize: { section: fontSizes.section, small: fontSizes.small }
  });
  
  // Results Summary
  if (a3Data.resultsAndLessonsLearned?.resultsSummary) {
    doc.setFontSize(fontSizes.content);
    const resultsLines = wrapText(doc, a3Data.resultsAndLessonsLearned.resultsSummary, contentWidth - 4);
    resultsLines.forEach(line => {
      doc.text(line, margin + 2, fullWidthY + 4);
      fullWidthY += lineHeight;
    });
    fullWidthY += 4;
  }
  
  // Lessons Learned Table
  if (a3Data.resultsAndLessonsLearned?.lessonsLearned && a3Data.resultsAndLessonsLearned.lessonsLearned.length > 0) {
    const tableWidth = contentWidth * 0.83;
    autoTable(doc, {
      startY: fullWidthY,
      head: [['Lesson', 'Category']],
      body: a3Data.resultsAndLessonsLearned.lessonsLearned.map(l => [l.lesson || '', l.category || '']),
      theme: 'grid',
      headStyles: { fillColor: accentColor, fontSize: fontSizes.content, fontStyle: 'bold' },
      bodyStyles: { fontSize: fontSizes.content },
      columnStyles: {
        0: { cellWidth: tableWidth * 0.70 },
        1: { cellWidth: tableWidth * 0.30 }
      },
      margin: { left: margin }
    });
    fullWidthY = (doc as any).lastAutoTable.finalY + 5;
  }
  
  // Validated Savings Box
  if (a3Data.validatedSavings !== undefined && a3Data.validatedSavings !== null) {
    const savingsBoxWidth = 60;
    const savingsBoxHeight = 20;
    const savingsX = pageWidth - margin - savingsBoxWidth;
    const savingsY = fullWidthY - (a3Data.resultsAndLessonsLearned?.lessonsLearned?.length ? 25 : 0);
    
    doc.setFillColor(...accentColor);
    doc.roundedRect(savingsX, savingsY, savingsBoxWidth, savingsBoxHeight, 3, 3, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`$${a3Data.validatedSavings.toLocaleString()}`, savingsX + savingsBoxWidth / 2, savingsY + 10, { align: 'center' });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Annualized Savings', savingsX + savingsBoxWidth / 2, savingsY + 16, { align: 'center' });
  }
  
  return fullWidthY;
};

// Helper: Render footer
const renderFooter = (
  doc: jsPDF,
  a3Data: A3Data,
  margin: number,
  pageWidth: number,
  pageHeight: number,
  footerFontSize: number
) => {
  doc.setTextColor(128, 128, 128);
  doc.setFontSize(footerFontSize);
  doc.text(`A3 ID: ${a3Data.a3Id}`, margin, pageHeight - 5);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
  doc.text('Page 1', pageWidth - margin, pageHeight - 5, { align: 'right' });
};

export const exportA3ToPdf = (a3Data: A3Data) => {
  // Use landscape orientation for A3 format with larger page size
  const doc = new jsPDF('landscape', 'mm', 'a3');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  const colWidth = (contentWidth - 10) / 2;
  let yPos = margin;

  // Colors
  const headerBg: [number, number, number] = [240, 240, 240];
  const accentColor: [number, number, number] = [11, 49, 74];
  const redColor: [number, number, number] = [220, 38, 38];
  const greenColor: [number, number, number] = [34, 197, 94];

  // Font sizes
  const fontSizes = {
    title: 20,
    section: 12,
    content: 10,
    small: 9,
    footer: 8
  };
  
  const LINE_HEIGHT = 6;

  // Render header section
  yPos = drawHeaderSection(doc, a3Data, margin, contentWidth, accentColor, greenColor, {
    title: fontSizes.title,
    small: fontSizes.small,
    content: fontSizes.content
  });

  // Two-column layout
  const leftColX = margin;
  const rightColX = margin + colWidth + 5;
  let leftY = yPos;
  let rightY = yPos;

  // Left column sections
  leftY = renderDefineProblemSection(doc, a3Data, leftColX, leftY, colWidth, LINE_HEIGHT, headerBg, accentColor, fontSizes);
  leftY = renderCurrentStateSection(doc, a3Data, leftColX, leftY, colWidth, LINE_HEIGHT, headerBg, accentColor, redColor, fontSizes);
  leftY = renderGoalTargetStateSection(doc, a3Data, leftColX, leftY, colWidth, LINE_HEIGHT, headerBg, accentColor, greenColor, fontSizes);

  // Right column sections
  rightY = renderCauseAnalysisSection(doc, a3Data, rightColX, rightY, colWidth, pageWidth, headerBg, accentColor, fontSizes);
  rightY = renderCountermeasuresSection(doc, a3Data, rightColX, rightY, colWidth, pageWidth, headerBg, accentColor, fontSizes);

  // Full width sections
  let fullWidthY = Math.max(leftY, rightY) + 5;
  fullWidthY = renderActionPlanSection(doc, a3Data, margin, fullWidthY, contentWidth, headerBg, accentColor, fontSizes);
  renderResultsAndLessonsSection(doc, a3Data, margin, fullWidthY, contentWidth, pageWidth, LINE_HEIGHT, headerBg, accentColor, fontSizes);

  // Footer
  renderFooter(doc, a3Data, margin, pageWidth, pageHeight, fontSizes.footer);

  // Save the PDF
  const fileName = `A3_${a3Data.title?.replace(/[^a-z0-9]/gi, '_').substring(0, 30) || a3Data.a3Id}.pdf`;
  doc.save(fileName);
};
